import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CreditCard,
  IndianRupee,
  Save,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import toast from 'react-hot-toast';
import { settingsApi } from '@/lib/api';

interface PaymentSettings {
  // Deposit Limits
  minDeposit: number;
  maxDeposit: number;
  
  // Withdrawal Limits
  minWithdrawal: number;
  maxWithdrawal: number;
}

const initialSettings: PaymentSettings = {
  minDeposit: 100,
  maxDeposit: 50000,
  minWithdrawal: 200,
  maxWithdrawal: 100000,
};

export default function PaymentSettings() {
  const [settings, setSettings] = useState(initialSettings);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsFetching(true);
      const response = await settingsApi.getSettings();
      const data = response.data.data;

      setSettings({
        minDeposit: data.minDeposit ?? 100,
        maxDeposit: data.maxDeposit ?? 50000,
        minWithdrawal: data.minWithdrawal ?? 200,
        maxWithdrawal: data.maxWithdrawal ?? 100000,
      });
    } catch (error: any) {
      console.error('Failed to fetch payment settings:', error);
      toast.error(error.response?.data?.message || 'Failed to load payment settings');
    } finally {
      setIsFetching(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      await settingsApi.updateSettings({
        minDeposit: settings.minDeposit,
        maxDeposit: settings.maxDeposit,
        minWithdrawal: settings.minWithdrawal,
        maxWithdrawal: settings.maxWithdrawal,
      });
      
      toast.success('Payment settings saved successfully');
    } catch (error: any) {
      console.error('Failed to save payment settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save payment settings');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex h-[60vh] w-full items-center justify-center rounded-xl bg-slate-900/50 border border-slate-800">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-violet-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-violet-400" />
            Payment Settings
          </h1>
          <p className="text-slate-400 mt-1">Configure deposit limits and withdrawal limits</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deposit Settings */}
        <Card variant="glass" className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-400" />
            Deposit Limits
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300">
                Minimum Deposit (₹)
              </label>
              <Input
                type="number"
                value={settings.minDeposit}
                onChange={(e) =>
                  setSettings({ ...settings, minDeposit: parseInt(e.target.value) })
                }
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum amount a user can deposit per transaction</p>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-300">
                Maximum Deposit (₹)
              </label>
              <Input
                type="number"
                value={settings.maxDeposit}
                onChange={(e) =>
                  setSettings({ ...settings, maxDeposit: parseInt(e.target.value) })
                }
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Maximum amount a user can deposit per transaction</p>
            </div>
          </div>
        </Card>

        {/* Withdrawal Settings */}
        <Card variant="glass" className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-violet-400" />
            Withdrawal Limits & Rules
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-300">
                  Min Withdrawal (₹)
                </label>
                <Input
                  type="number"
                  value={settings.minWithdrawal}
                  onChange={(e) =>
                    setSettings({ ...settings, minWithdrawal: parseInt(e.target.value) })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300">
                  Max Withdrawal (₹)
                </label>
                <Input
                  type="number"
                  value={settings.maxWithdrawal}
                  onChange={(e) =>
                    setSettings({ ...settings, maxWithdrawal: parseInt(e.target.value) })
                  }
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </motion.div>
  );
}
